import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Star, Trash2, Youtube, ExternalLink, Info } from "lucide-react";

const empty = { author_name: "", company: "", content: "", rating: 5, youtube_url: "", source: "manuel", avatar_url: "", review_url: "", review_date: "" };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.author_name || !form.content) return toast.error("Auteur et contenu requis");
    try {
      await api.post("/admin/testimonials", { ...form, rating: parseInt(form.rating) });
      setOpen(false); setForm(empty); load();
      toast.success("Témoignage ajouté");
    } catch { toast.error("Échec"); }
  };

  const toggle = async (id, published) => {
    try { await api.patch(`/admin/testimonials/${id}`, { published }); load(); } catch { toast.error("Échec"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); load(); } catch { toast.error("Échec"); }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Avis & témoignages</h1>
          <p className="text-sm text-[#64748B] mt-1">Saisissez manuellement (depuis Google, LinkedIn…) ou ajoutez des vidéos YouTube.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
          <DialogTrigger asChild>
            <Button data-testid="testimonial-add-btn" className="bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-bold"><Plus size={16} className="mr-1.5" />Ajouter un avis</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Nouvel avis / témoignage</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Auteur *</Label><Input data-testid="testimonial-author" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} placeholder="Ex : Marie Dupont" /></div>
                <div><Label>Entreprise / poste</Label><Input data-testid="testimonial-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Ex : Dirigeante PME" /></div>
              </div>
              <div><Label>Contenu de l'avis *</Label><Textarea data-testid="testimonial-content" rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Texte de l'avis…" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Note</Label><Input data-testid="testimonial-rating" type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
                <div><Label>Source</Label>
                  <select data-testid="testimonial-source" className="w-full h-9 border border-input rounded-md px-2 text-sm" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                    <option value="manuel">Manuel</option><option value="google">Google</option><option value="linkedin">LinkedIn</option><option value="youtube">YouTube</option>
                  </select>
                </div>
                <div><Label>Date</Label><Input data-testid="testimonial-date" type="date" value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} /></div>
              </div>
              <div className="bg-[#06B6D4]/5 border border-[#06B6D4]/20 rounded-xl p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#06B6D4]">Pour un avis Google</div>
                <div><Label className="text-xs">Lien direct vers l'avis</Label><Input data-testid="testimonial-review-url" value={form.review_url} onChange={(e) => setForm({ ...form, review_url: e.target.value })} placeholder="https://maps.google.com/?cid=..." /></div>
                <div><Label className="text-xs">Photo de profil (URL avatar)</Label><Input data-testid="testimonial-avatar" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://lh3.googleusercontent.com/..." /></div>
              </div>
              <div><Label>Lien vidéo YouTube (optionnel)</Label><Input data-testid="testimonial-youtube" placeholder="https://youtube.com/watch?v=..." value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} /></div>
            </div>
            <DialogFooter><Button data-testid="testimonial-save" onClick={create} className="bg-[#0A1128] hover:bg-[#14213d] text-white">Enregistrer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info banner Google import (coming) */}
      <div className="mt-3 bg-[#FFDCA8]/30 border border-[#FFDCA8] rounded-xl px-4 py-2.5 flex items-start gap-2.5">
        <Info size={14} className="text-[#92400E] shrink-0 mt-0.5" />
        <div className="text-xs text-[#92400E] leading-relaxed">
          <strong>Import automatique Google bientôt disponible.</strong> En attendant, copiez-collez vos avis Google ci-dessus en sélectionnant <em>Source: Google</em> + collez le lien direct vers l'avis et l'URL de la photo de profil de l'auteur (clic-droit → "Copier l'adresse de l'image" sur Google Maps).
          {" "}<span className="opacity-80">Configurez votre Place ID dans <a href="/admin/parametres" className="underline font-bold">Paramètres</a>.</span>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && <div className="text-[#64748B] text-sm col-span-full bg-white rounded-2xl p-8 text-center border border-dashed border-black/10">Aucun avis. Ajoutez le premier !</div>}
        {items.map((t) => (
          <div key={t.id} data-testid={`testimonial-${t.id}`} className="bg-white border border-black/8 rounded-2xl p-5 relative overflow-hidden">
            {t.source === "google" && <span className="absolute top-3 right-3 text-[9px] uppercase font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Google</span>}
            {t.source === "linkedin" && <span className="absolute top-3 right-3 text-[9px] uppercase font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">LinkedIn</span>}
            <div className="flex items-center gap-1">{[...Array(t.rating || 5)].map((_, i) => <Star key={i} size={14} className="fill-[#FFDCA8] stroke-[#FFDCA8]" />)}</div>
            <p className="mt-3 text-sm text-[#0A1128] leading-relaxed line-clamp-5">{t.content}</p>
            <div className="mt-4 flex items-center gap-3">
              {t.avatar_url ? (
                <img src={t.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover border border-black/5" onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#06B6D4]/15 flex items-center justify-center text-[#06B6D4] font-extrabold text-sm">{(t.author_name || "?").slice(0, 1).toUpperCase()}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="display font-bold text-sm text-[#0A1128] truncate">{t.author_name}</div>
                <div className="text-xs text-[#64748B] truncate">{t.company}{t.review_date ? ` · ${t.review_date}` : ""}</div>
              </div>
              {t.youtube_url && <Youtube size={16} className="text-[#FF0000]" />}
              {t.review_url && <a href={t.review_url} target="_blank" rel="noreferrer" className="text-[#06B6D4] hover:text-[#67E8F9]" title="Voir l'avis original"><ExternalLink size={14} /></a>}
            </div>
            <div className="mt-3 pt-3 border-t border-black/5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-[10px] text-[#64748B]"><Switch checked={t.published} onCheckedChange={(v) => toggle(t.id, v)} data-testid={`testimonial-publish-${t.id}`} /><span>{t.published ? "Publié" : "Brouillon"}</span></div>
              <button onClick={() => del(t.id)} className="text-[#94a3b8] hover:text-red-500 p-1" title="Supprimer"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
